/**
 * DBC abilities store — R232q5aS Wave 5.
 *
 * On first login per session (per classId) fetches the canonical ability
 * list from the server `/dbc/abilities/:classMask` endpoint and caches it
 * in zustand. The ActionBar / Spellbook / TalentTree all hover-tooltip
 * pull from this cache so we don't round-trip per-icon.
 *
 * Wire-up:
 *   - World.tsx triggers `ensureClassAbilities(classId)` once `draft.classId`
 *     is set.
 *   - ActionBar tooltip overlays `dbcDescription` + `dbcRank` over the
 *     curated client-side ability metadata.
 *   - Spellbook decoration of unlearned abilities reads `dbcSpellId` to
 *     query `/dbc/spell/:id` for raw stats.
 */

import { create } from 'zustand';
import type { ClassId } from '../data/classes.js';

const API_BASE = import.meta.env.DEV ? 'http://localhost:2570' : '/aiwari-api';

const CLASS_MASK: Record<ClassId, number> = {
  warrior: 1,  paladin: 2,  ranger: 4,  rogue: 8,
  cleric:  16, shaman:  64, mage:   128, warlock: 256, druid: 1024,
  // R232q33: WotLK Death Knight class_mask = 32 (WotLK 3.3.5a Spell.dbc).
  deathknight: 32,
};

export interface DbcAbilityRow {
  id: string;
  type: string;
  cooldownMs: number;
  cost: number;
  power: number;
  range: number;
  aoeRadius: number;
  durationMs: number;
  windupMs: number;
  dbcSpellId: number | null;
  dbcName: string | null;
  dbcRank: string | null;
  dbcDescription: string | null;
  dbcTooltip: string | null;
  dbcSpellIcon: number | null;
}

interface DbcAbilitiesStore {
  /** Cached per-class ability list (populated once per session). */
  byClass: Partial<Record<ClassId, DbcAbilityRow[]>>;
  /** True when a fetch is currently in-flight for this class. */
  inflight: Partial<Record<ClassId, boolean>>;
  /** Fire-and-forget fetcher; idempotent per classId. */
  ensureClassAbilities: (classId: ClassId) => void;
  /** Look up a single ability row by curated id (e.g. "fireball"). */
  abilityByCuratedId: (classId: ClassId, abilityId: string) => DbcAbilityRow | null;
}

export const useDbcAbilities = create<DbcAbilitiesStore>((set, get) => ({
  byClass: {},
  inflight: {},
  ensureClassAbilities: (classId) => {
    const cur = get();
    if (cur.byClass[classId] || cur.inflight[classId]) return;
    const mask = CLASS_MASK[classId];
    if (!mask) return;
    set({ inflight: { ...cur.inflight, [classId]: true } });
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/dbc/abilities/${mask}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as { abilities: DbcAbilityRow[] };
        set((s) => ({
          byClass: { ...s.byClass, [classId]: json.abilities ?? [] },
          inflight: { ...s.inflight, [classId]: false },
        }));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[dbc-abilities] fetch failed', classId, err);
        set((s) => ({ inflight: { ...s.inflight, [classId]: false } }));
      }
    })();
  },
  abilityByCuratedId: (classId, abilityId) => {
    const list = get().byClass[classId];
    if (!list) return null;
    return list.find((a) => a.id === abilityId) ?? null;
  },
}));

/** One-shot fetch of a single DBC spell row (for tooltips outside the bar). */
const spellRowCache = new Map<number, unknown>();
export async function fetchDbcSpell(spellId: number): Promise<unknown | null> {
  if (!Number.isFinite(spellId) || spellId <= 0) return null;
  if (spellRowCache.has(spellId)) return spellRowCache.get(spellId) ?? null;
  try {
    const res = await fetch(`${API_BASE}/dbc/spell/${spellId}`);
    if (!res.ok) { spellRowCache.set(spellId, null); return null; }
    const row = await res.json();
    spellRowCache.set(spellId, row);
    return row;
  } catch {
    spellRowCache.set(spellId, null);
    return null;
  }
}

/**
 * R232q35: Client-side `lookupSpell(id)` mirror of the server registry.
 *
 * Synchronous read of the in-memory cache populated by `fetchDbcSpell`.
 * Returns `null` when the row hasn't been fetched yet — caller should
 * `await fetchDbcSpell(id)` first if they need a guaranteed answer
 * (e.g. on tooltip-hover rendering paths).
 *
 * Falls back to `name_en` when `name_ru` is empty (downstream LLM pass
 * will fill `name_ru`; until then English is safer than blank).
 *
 * TODO/AIWARI-LEGAL: replace BORROWED Blizzard 3.3.5a labels before launch.
 */
export interface DbcSpellSummary {
  id: number;
  name: string;
  description: string | null;
  rank: string | null;
  spellIcon: number | null;
}

export function lookupSpell(spellId: number): DbcSpellSummary | null {
  if (!Number.isFinite(spellId) || spellId <= 0) return null;
  const row = spellRowCache.get(spellId);
  if (!row || typeof row !== 'object') return null;
  // Server endpoint returns `{ name_en, name_ru, description, rank_en, … }`.
  const r = row as {
    id?: number;
    name_en?: string;
    name_ru?: string;
    description?: string | null;
    rank_en?: string | null;
    spell_icon_id?: number | null;
  };
  return {
    id: r.id ?? spellId,
    name: (r.name_ru && r.name_ru.length > 0) ? r.name_ru : (r.name_en ?? `Spell ${spellId}`),
    description: r.description ?? null,
    rank: r.rank_en ?? null,
    spellIcon: r.spell_icon_id ?? null,
  };
}
